import { isDeliveryFrozen } from "@/forwards2/deals/deliveryFrozen";
import { type DraftConflict, mergeDraft } from "@/forwards2/deals/draftMerge";
import type {
  DeliveryResponse,
  PapMonthStatus,
  PapScheduleUpdateRequest,
} from "@/forwards2/forwardsTypes";
import { toFraction, toPercentText } from "@/forwards2/percentUtils";
import { isValidUnitPrice } from "@/forwards2/priceUtils";

/**
 * The nominated schedule card's draft: every contract month's target (MWh) and sell price (€/MWh),
 * and the tolerance (percent), as one flat record of field key to value, so the 409 merge
 * (`draftMerge`) treats each on its own. Null is an empty input.
 */
export type ScheduleValue = number | null;
export type ScheduleDraft = Record<string, ScheduleValue>;

export const TOLERANCE_FIELD = "tolerance";
export const targetField = (month: string): string => `target:${month}`;
export const priceField = (month: string): string => `price:${month}`;

/** One contract month as the card edits it. */
export interface ScheduleMonth {
  month: string;
  deliveryId: number;
  target: number;
  price: number | null;
  /** Shipped (or voided) months are history: their target is read-only, their price a super admin's correction. */
  shipped: boolean;
}

/**
 * What the session may change on the card.
 *
 * @property schedule - The open months' targets and prices and the tolerance (`can_edit_pap_schedule`).
 * @property correctsShipped - A shipped month's price (`can_correct_shipped`), on a finished contract too.
 */
export interface ScheduleAccess {
  schedule: boolean;
  correctsShipped: boolean;
}

/** Whether the month's price input is open to the session. */
export const isPriceEditable = (
  month: ScheduleMonth,
  access: ScheduleAccess
): boolean => (month.shipped ? access.correctsShipped : access.schedule);

/**
 * The months as the card edits them. The monitoring names each month and the deal holds its
 * values, so the values and the `plan_version` a save carries come from the same read; the two
 * reads are separate, so either one having seen a month ship makes it read-only.
 */
export function scheduleMonths(
  months: readonly PapMonthStatus[],
  deliveries: readonly DeliveryResponse[]
): ScheduleMonth[] {
  const byId = new Map(deliveries.map((delivery) => [delivery.id, delivery]));
  return months.map((month) => {
    const delivery = byId.get(month.delivery_id);
    const price = delivery?.unit_price ?? null;
    return {
      month: month.month,
      deliveryId: month.delivery_id,
      target: delivery?.pap_target_mwh ?? month.target_mwh,
      price: price === null ? null : Number(price),
      shipped:
        isDeliveryFrozen(month.transaction_status) ||
        delivery === undefined ||
        isDeliveryFrozen(delivery.transaction_status),
    };
  });
}

/** The saved schedule, in the draft's terms. */
export function savedSchedule(
  months: readonly ScheduleMonth[],
  tolerance: string | null
): ScheduleDraft {
  const draft: ScheduleDraft = {
    [TOLERANCE_FIELD]:
      tolerance === null ? null : Number(toPercentText(tolerance)),
  };
  for (const month of months) {
    draft[targetField(month.month)] = month.target;
    draft[priceField(month.month)] = month.price;
  }
  return draft;
}

/** A shipped month's target, and its price unless the session corrects shipped months: no save may change them. */
export const lockedScheduleFields = (
  months: readonly ScheduleMonth[],
  access: ScheduleAccess
): Set<string> =>
  new Set(
    months
      .filter((month) => month.shipped)
      .flatMap((month) =>
        access.correctsShipped
          ? [targetField(month.month)]
          : [targetField(month.month), priceField(month.month)]
      )
  );

/**
 * Whether a typed sell price is one the schedule save takes: above zero with at most three
 * decimals. A price is never cleared, so an empty field is valid only on a month that has none yet.
 */
export const isSchedulePriceValid = (
  value: ScheduleValue,
  saved: ScheduleValue
): boolean =>
  value === null ? saved === null : value > 0 && isValidUnitPrice(value);

/**
 * The one request that saves the draft: each open month's target and every price that is set, the
 * tolerance as the column's fraction, and the `plan_version` the draft was composed against.
 * Shipped months are left out — the backend keeps an omitted month as it is — but for their prices
 * when the session corrects them; what the session may not change is never sent.
 */
export function scheduleRequest(
  draft: Readonly<ScheduleDraft>,
  months: readonly ScheduleMonth[],
  planVersion: number,
  access: ScheduleAccess
): PapScheduleUpdateRequest {
  const open = access.schedule ? months.filter((month) => !month.shipped) : [];
  const priced = months.filter((month) => isPriceEditable(month, access));
  const prices = priced.flatMap((month) => {
    const price = draft[priceField(month.month)];
    return price === null || price === undefined
      ? []
      : [[month.month, String(price)] as const];
  });
  const tolerance = draft[TOLERANCE_FIELD];
  return {
    plan_version: planVersion,
    // Only months the draft captured: a month it never saw must stay as saved, not become 0.
    monthly_targets: Object.fromEntries(
      open.flatMap((month) => {
        const target = draft[targetField(month.month)];
        return target === null || target === undefined
          ? []
          : [[month.month, target] as const];
      })
    ),
    ...(prices.length > 0
      ? { monthly_prices: Object.fromEntries(prices) }
      : {}),
    ...(tolerance === null || tolerance === undefined || !access.schedule
      ? {}
      : { volume_tolerance: toFraction(tolerance) }),
  };
}

/**
 * The draft after a 409: merged onto the schedule as it is saved now (`mergeDraft`).
 *
 * @property takenOver - How many fields now hold someone else's saved value.
 * @property shippedMonths - Months that shipped meanwhile and had edits of the user's, now dropped.
 */
export interface ScheduleMerge {
  draft: ScheduleDraft;
  conflicts: Record<string, DraftConflict<ScheduleValue>>;
  takenOver: number;
  shippedMonths: string[];
}

export function mergeSchedule(
  base: Readonly<ScheduleDraft>,
  draft: Readonly<ScheduleDraft>,
  latestMonths: readonly ScheduleMonth[],
  latestTolerance: string | null,
  access: ScheduleAccess
): ScheduleMerge {
  const merged = mergeDraft({
    base,
    theirs: savedSchedule(latestMonths, latestTolerance),
    mine: draft,
    locked: lockedScheduleFields(latestMonths, access),
  });
  const droppedFields = new Set(merged.dropped);
  return {
    draft: merged.values,
    conflicts: merged.conflicts,
    takenOver: merged.takenOver.length,
    shippedMonths: latestMonths
      .filter(
        (month) =>
          droppedFields.has(targetField(month.month)) ||
          droppedFields.has(priceField(month.month))
      )
      .map((month) => month.month),
  };
}
